import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button, Surface, Txt } from '@vst/beam';
import { Trans, useTranslation } from 'react-i18next';

import { ROUTES } from '@app/router/constants';
import { useNavigate } from '@app/hooks';
import { selectBillingInfo } from '@app/redux/billingInfo';
import { Issuer, selectConfig } from '@app/redux/config';
import { selectLocale } from '@app/redux/locale';
import { selectUserInfo } from '@app/redux/userInfo';
import { postOverviewMessage } from '@app/redux/utils';
import { getUnpaidInvoice } from '@app/utils';
import { EventTypes } from '@app/types';

import { Locale } from '@mfe/shared/schema-types';

import { goToPayment } from '../../../Billing';
import { OverviewAutoPayMethod } from './autopay-method';
import { BalanceCardError } from './balance-card-error';
import { LoadingContent } from './loading-content';
import { PreinstallBalanceCard } from './preinstall-balance-card';
import { getBadgeProps, getPaymentDate, InvoiceCurrentStatus } from './utils';

import styles from './balance-card.module.scss';

export const BalanceCard = () => {
  const { t } = useTranslation('Overview', { keyPrefix: 'balanceCard' });

  const { goTo } = useNavigate();
  const dispatch = useDispatch();

  const {
    userInfo: { isPreInstall },
  } = useSelector(selectUserInfo);

  const {
    locale: { userLocale: locale },
  } = useSelector(selectLocale);

  const {
    overviewLoading,
    billingInfo: { invoices, totalBalance, billingAccount },
    errors,
  } = useSelector(selectBillingInfo);

  const invoice = React.useMemo(() => getUnpaidInvoice(locale, invoices) ?? invoices[0], [invoices, locale]);

  const badgeProps = React.useMemo(
    () =>
      getBadgeProps(
        {
          invoiceStatus: invoice?.invoiceStatus,
          invoiceSettlementStatus: invoice?.invoiceSettlementStatus,
        },
        t
      ),
    [invoice, t]
  );

  const { issuer } = useSelector(selectConfig);

  const isSalesforce = issuer === Issuer.Salesforce;

  const isPaymentButtonDisabled = (locale === Locale.PtBr && totalBalance.value === 0) || isSalesforce;

  const showPaymentDueDateInfo = badgeProps.status && badgeProps.status !== InvoiceCurrentStatus.PastDue;

  const showBadge = badgeProps.status && invoices.length > 0;

  const makeAPaymentHandler = () => {
    dispatch(postOverviewMessage({ eventType: EventTypes.PayBill }));
    goToPayment(dispatch, invoice);
    goTo(ROUTES.BILLING);
  };

  if (overviewLoading) {
    return <LoadingContent />;
  }

  if (isPreInstall || (!isPreInstall && invoices.length < 1)) {
    return <PreinstallBalanceCard />;
  }

  if (errors) {
    return <BalanceCardError />;
  }

  return (
    <div className={styles['balanceCard']} data-cy="balance-card">
      <Surface theme="dark" className={styles['content']}>
        <div className={styles['header']}>
          <div>
            <Txt variant="bodyLargeBold" data-cy="title">
              {t('title')}
            </Txt>
            <Txt variant="heading1" data-cy="balance-data">
              {totalBalance.text}
            </Txt>
          </div>
          {showBadge && <Badge data-cy="badge" size="small" showIcon={false} emphasis="heavy" {...badgeProps} />}
        </div>

        {showPaymentDueDateInfo && (
          <Txt variant="smallRegular" data-cy="dueDate-info">
            <Trans
              ns="Summary"
              i18nKey="overviewPage.paymentDate"
              values={getPaymentDate(
                {
                  invoiceStatus: invoice?.invoiceStatus,
                  invoiceSettlementStatus: invoice?.invoiceSettlementStatus,
                },
                billingAccount
              )}
            />
          </Txt>
        )}

        <div className={styles['autoPay']} data-cy="autoPay">
          <OverviewAutoPayMethod />
        </div>
      </Surface>

      <div className={styles['balanceFooter']} data-cy="card-footer">
        <Button
          data-cy="make-payment-button"
          variant="primary"
          theme="color"
          fluid
          disabled={isPaymentButtonDisabled}
          onClick={makeAPaymentHandler}
        >
          {t('makePaymentButton')}
        </Button>
      </div>
    </div>
  );
};
